import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { X, Calculator, Check } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../services/api'
import { useTelegram } from '../../hooks/useTelegram'
import { useAuth } from '../../hooks/useAuth'
import { calculateKbju, isValidKbjuInput, CALC_ACTIVITY, CALC_GOAL, KBJU_INPUT_RANGES, type CalcActivity, type CalcGoal, type CalcSex } from '@tfa/shared'

const F = 'Roboto, sans-serif'

/**
 * #537: своя норма КБЖУ — для тарифов без куратора.
 *
 * На ступенях норму согласует куратор, и дневник честно писал «Норму задаёт
 * куратор». В Клубе куратора нет, и эта надпись обещала человека, который
 * никогда не придёт: норма не появлялась вообще, а шкалы питания стояли на
 * дефолтах. Здесь человек задаёт её сам, а рядом — готовый калькулятор.
 */
export const KBJU_CALCULATOR_URL = 'https://tulupavel.ru/calc_kbzhu'

type DayType = 'REGULAR' | 'TRAINING'

interface GoalForm {
  calories: string
  protein: string
  fat: string
  carbs: string
  fiber: string
}

const EMPTY: GoalForm = { calories: '', protein: '', fat: '', carbs: '', fiber: '' }

const RANGES = {
  calories: { min: 200, max: 20000, unit: 'ккал' },
  protein: { min: 0, max: 2000, unit: 'г' },
  fat: { min: 0, max: 2000, unit: 'г' },
  carbs: { min: 0, max: 2000, unit: 'г' },
  fiber: { min: 0, max: 2000, unit: 'г' },
} as const

type FieldKey = keyof typeof RANGES

const LABELS: Record<FieldKey, string> = {
  calories: 'Калории',
  protein: 'Белки',
  fat: 'Жиры',
  carbs: 'Углеводы',
  fiber: 'Клетчатка',
}

const parseNum = (raw: string): number | undefined => {
  const t = raw.trim().replace(',', '.')
  if (!t) return undefined
  const n = Number(t)
  return Number.isFinite(n) ? Math.round(n) : undefined
}

function fieldError(key: FieldKey, raw: string): string | undefined {
  const required = key === 'calories'
  const n = parseNum(raw)
  if (n === undefined) return required ? `Укажи ${LABELS[key].toLowerCase()}` : undefined
  if (n < RANGES[key].min) return `Не меньше ${RANGES[key].min}`
  if (n > RANGES[key].max) return `Не больше ${RANGES[key].max}`
  return undefined
}

interface Props {
  open: boolean
  onClose: () => void
}

export function NutritionGoalSheet({ open, onClose }: Props) {
  const queryClient = useQueryClient()
  const { openLink, impactHaptic } = useTelegram()
  const [regular, setRegular] = useState<GoalForm>(EMPTY)
  const [training, setTraining] = useState<GoalForm>(EMPTY)
  // Тренировочный день отличается от обычного далеко не у всех — второй блок
  // раскрывается по желанию, чтобы не требовать десять чисел вместо пяти.
  const [trainingOpen, setTrainingOpen] = useState(false)
  const [touched, setTouched] = useState(false)
  // #542 (три тарифа): калькулятор нормы встроен — результат сразу становится
  // нормой обычного дня; внешняя страница остаётся запасной ссылкой.
  const { user } = useAuth()
  const [calcOpen, setCalcOpen] = useState(false)
  const [calc, setCalc] = useState<{ sex: CalcSex; age: string; heightCm: string; weightKg: string; activity: CalcActivity; goal: CalcGoal }>(() => ({
    sex: ((user as { gender?: string } | undefined)?.gender === 'FEMALE' ? 'FEMALE' : 'MALE'),
    age: '', heightCm: '', weightKg: '', activity: 'medium', goal: 'keep',
  }))
  const calcInput = { sex: calc.sex, age: Number(calc.age), heightCm: Number(calc.heightCm), weightKg: Number(calc.weightKg.replace(',', '.')), activity: calc.activity, goal: calc.goal }
  const calcValid = isValidKbjuInput(calcInput)
  const applyCalc = () => {
    if (!calcValid) return
    const r = calculateKbju(calcInput)
    setRegular({ calories: String(r.calories), protein: String(r.protein), fat: String(r.fat), carbs: String(r.carbs), fiber: regular.fiber })
    setTouched(true)
    setCalcOpen(false)
    impactHaptic('medium')
    toast.success(`Норма посчитана: ${r.calories} ккал · Б ${r.protein} · Ж ${r.fat} · У ${r.carbs}`)
  }

  const { data } = useQuery({
    queryKey: ['diary', 'nutrition-goals'],
    queryFn: () => api.getMyNutritionGoals(),
    enabled: open,
  })

  useEffect(() => {
    if (!open || !data) return
    const toForm = (t: DayType): GoalForm => {
      const g = data.goals.find((x) => x.dayType === t)
      if (!g) return EMPTY
      return {
        calories: String(g.calories),
        protein: String(g.protein),
        fat: String(g.fat),
        carbs: String(g.carbs),
        fiber: g.fiber != null ? String(g.fiber) : '',
      }
    }
    setRegular(toForm('REGULAR'))
    const t = toForm('TRAINING')
    setTraining(t)
    setTrainingOpen(t.calories !== '')
    setTouched(false)
  }, [open, data])

  const save = useMutation({
    mutationFn: () => {
      const build = (f: GoalForm, dayType: DayType) => ({
        dayType,
        calories: parseNum(f.calories) ?? 0,
        protein: parseNum(f.protein) ?? 0,
        fat: parseNum(f.fat) ?? 0,
        carbs: parseNum(f.carbs) ?? 0,
        ...(parseNum(f.fiber) != null ? { fiber: parseNum(f.fiber)! } : {}),
      })
      const goals = [build(regular, 'REGULAR')]
      if (trainingOpen && training.calories.trim() !== '') goals.push(build(training, 'TRAINING'))
      return api.saveMyNutritionGoals(goals)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diary'] })
      queryClient.invalidateQueries({ queryKey: ['diary', 'nutrition-goals'] })
      toast.success('Норма сохранена')
      onClose()
    },
    onError: () => toast.error('Не удалось сохранить норму'),
  })

  const regularErrors = (Object.keys(RANGES) as FieldKey[])
    .map((k) => fieldError(k, regular[k]))
    .filter(Boolean)
  const trainingErrors = trainingOpen && training.calories.trim() !== ''
    ? (Object.keys(RANGES) as FieldKey[]).map((k) => fieldError(k, training[k])).filter(Boolean)
    : []
  const canSave = regularErrors.length === 0 && trainingErrors.length === 0 && !save.isPending

  const renderFields = (form: GoalForm, setForm: (f: GoalForm) => void, prefix: string) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {(Object.keys(RANGES) as FieldKey[]).map((key) => {
        const err = touched ? fieldError(key, form[key]) : undefined
        return (
          <div key={key} style={{ gridColumn: key === 'calories' ? '1 / -1' : undefined }}>
            <label
              htmlFor={`${prefix}-${key}`}
              style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6B7B8D', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}
            >
              {LABELS[key]}, {RANGES[key].unit}
            </label>
            <input
              id={`${prefix}-${key}`}
              data-testid={`${prefix}-${key}`}
              type="text"
              inputMode="numeric"
              value={form[key]}
              placeholder={key === 'calories' ? '2100' : '—'}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '11px 12px', borderRadius: 12, fontFamily: F, fontSize: 16,
                color: '#1C2D41', background: 'rgba(255,255,255,0.9)',
                border: err ? '1px solid #E2574C' : '1px solid rgba(26,58,92,0.12)',
                outline: 'none',
              }}
            />
            {err && <div style={{ fontSize: 11, color: '#E2574C', marginTop: 3 }}>{err}</div>}
          </div>
        )
      })}
    </div>
  )

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 400,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontFamily: F,
          }}
        >
          <motion.div
            data-testid="modal-nutrition-goal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(30px)',
              borderTopLeftRadius: 26, borderTopRightRadius: 26,
              padding: '10px 16px calc(20px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '88vh', overflowY: 'auto',
            }}
          >
            <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(26,58,92,0.18)', margin: '0 auto 14px' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1C2D41', margin: 0 }}>Моя норма</h3>
              <button
                onClick={onClose}
                aria-label="Закрыть"
                style={{ border: 'none', background: 'transparent', padding: 4, cursor: 'pointer', color: '#6B7B8D' }}
              >
                <X size={20} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#6B7B8D', margin: '0 0 12px', lineHeight: 1.4 }}>
              Норму задаёшь сам — по ней считаются шкалы в питании. Поменять можно в любой момент.
            </p>

            <button
              data-testid="nutrition-goal-calculator"
              onClick={() => { impactHaptic('light'); setCalcOpen((v) => !v) }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', marginBottom: 14, borderRadius: 14, cursor: 'pointer',
                background: 'rgba(52,199,89,0.10)', border: '1px solid rgba(52,199,89,0.30)',
                fontFamily: F, textAlign: 'left',
              }}
            >
              <Calculator size={18} color="#1B9E44" />
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: '#1B5E20' }}>
                Посчитать свою норму калорий
              </span>
            </button>

            {calcOpen && (
              <div data-testid="nutrition-goal-calc" style={{ marginBottom: 14, padding: 12, borderRadius: 14, background: 'rgba(26,58,92,0.04)', border: '1px solid rgba(26,58,92,0.10)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label style={{ fontSize: 12, color: '#6B7B8D' }}>Пол
                    <select data-testid="calc-sex" value={calc.sex} onChange={(e) => setCalc({ ...calc, sex: e.target.value as CalcSex })} style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(26,58,92,0.15)', fontFamily: F, fontSize: 14 }}>
                      <option value="MALE">Мужской</option><option value="FEMALE">Женский</option>
                    </select>
                  </label>
                  <label style={{ fontSize: 12, color: '#6B7B8D' }}>Возраст
                    <input data-testid="calc-age" inputMode="numeric" value={calc.age} onChange={(e) => setCalc({ ...calc, age: e.target.value.replace(/\D/g, '') })} placeholder={`${KBJU_INPUT_RANGES.age.min}–${KBJU_INPUT_RANGES.age.max}`} style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(26,58,92,0.15)', fontFamily: F, fontSize: 14, boxSizing: 'border-box' }} />
                  </label>
                  <label style={{ fontSize: 12, color: '#6B7B8D' }}>Рост, см
                    <input data-testid="calc-height" inputMode="numeric" value={calc.heightCm} onChange={(e) => setCalc({ ...calc, heightCm: e.target.value.replace(/\D/g, '') })} placeholder="170" style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(26,58,92,0.15)', fontFamily: F, fontSize: 14, boxSizing: 'border-box' }} />
                  </label>
                  <label style={{ fontSize: 12, color: '#6B7B8D' }}>Вес, кг
                    <input data-testid="calc-weight" inputMode="decimal" value={calc.weightKg} onChange={(e) => setCalc({ ...calc, weightKg: e.target.value.replace(/[^\d.,]/g, '') })} placeholder="70" style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(26,58,92,0.15)', fontFamily: F, fontSize: 14, boxSizing: 'border-box' }} />
                  </label>
                </div>
                <label style={{ display: 'block', marginTop: 8, fontSize: 12, color: '#6B7B8D' }}>Активность
                  <select data-testid="calc-activity" value={calc.activity} onChange={(e) => setCalc({ ...calc, activity: e.target.value as CalcActivity })} style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(26,58,92,0.15)', fontFamily: F, fontSize: 14 }}>
                    {(Object.keys(CALC_ACTIVITY) as CalcActivity[]).map((k) => <option key={k} value={k}>{CALC_ACTIVITY[k].label}</option>)}
                  </select>
                </label>
                <label style={{ display: 'block', marginTop: 8, fontSize: 12, color: '#6B7B8D' }}>Цель
                  <select data-testid="calc-goal" value={calc.goal} onChange={(e) => setCalc({ ...calc, goal: e.target.value as CalcGoal })} style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(26,58,92,0.15)', fontFamily: F, fontSize: 14 }}>
                    {(Object.keys(CALC_GOAL) as CalcGoal[]).map((k) => <option key={k} value={k}>{CALC_GOAL[k].label}</option>)}
                  </select>
                </label>
                {calcValid && (() => { const r = calculateKbju(calcInput); return (
                  <div data-testid="calc-result" style={{ marginTop: 10, fontSize: 13, color: '#1C2D41', lineHeight: 1.4 }}>
                    <b>{r.calories} ккал</b> · Б {r.protein} · Ж {r.fat} · У {r.carbs} г<br />
                    <span style={{ fontSize: 11, color: '#6B7B8D' }}>Базовый обмен {r.bmr}, поддержка {r.tdee} ккал</span>
                  </div>
                ) })()}
                <button data-testid="calc-apply" disabled={!calcValid} onClick={applyCalc} style={{ marginTop: 10, width: '100%', height: 42, borderRadius: 12, border: 'none', cursor: calcValid ? 'pointer' : 'not-allowed', background: calcValid ? '#1B9E44' : 'rgba(26,58,92,0.12)', color: 'white', fontSize: 14, fontWeight: 700, fontFamily: F }}>
                  Поставить как норму
                </button>
                <button type="button" onClick={() => { impactHaptic('light'); openLink(KBJU_CALCULATOR_URL) }} style={{ marginTop: 6, width: '100%', background: 'transparent', border: 'none', color: '#6B7B8D', fontSize: 12, cursor: 'pointer', fontFamily: F }}>
                  Открыть калькулятор на сайте
                </button>
              </div>
            )}

            <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7B8D', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
              Обычный день
            </div>
            {renderFields(regular, setRegular, 'goal-regular')}

            <div style={{ marginTop: 16 }}>
              {trainingOpen ? (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7B8D', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                    День тренировки
                  </div>
                  {renderFields(training, setTraining, 'goal-training')}
                </>
              ) : (
                <button
                  data-testid="nutrition-goal-add-training"
                  onClick={() => setTrainingOpen(true)}
                  style={{
                    width: '100%', padding: '11px 14px', borderRadius: 12, cursor: 'pointer',
                    background: 'transparent', border: '1px dashed rgba(26,58,92,0.22)',
                    fontFamily: F, fontSize: 13, fontWeight: 600, color: '#4A6CF7',
                  }}
                >
                  + Отдельная норма на день тренировки
                </button>
              )}
            </div>

            <button
              data-testid="nutrition-goal-save"
              onClick={() => { setTouched(true); if (canSave) save.mutate() }}
              disabled={save.isPending}
              style={{
                width: '100%', marginTop: 18, padding: '14px 16px', borderRadius: 14,
                border: 'none', cursor: save.isPending ? 'default' : 'pointer',
                background: 'linear-gradient(135deg,#1B5E20,#34C759)', color: 'white',
                fontFamily: F, fontSize: 15, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: save.isPending ? 0.7 : 1,
              }}
            >
              <Check size={17} />
              {save.isPending ? 'Сохраняем…' : 'Сохранить норму'}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
